import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useLanguage} from '../../../_gori/i18n/Metronici18n'
import LanguageToggle from '../../modules/common/components/LanguageToggle'
import DemoPanel from '../../modules/demo/DemoPanel'

interface TopicsLocationState {
  preSelectedSubtopics?: string[]
  autoStart?: boolean
  newlyAddedSubtopics?: string[]
}

const DemoTopicsPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const {language: currentLanguage} = useLanguage()

  // Map language to supported video language codes
  const language = useMemo(() => {
    if (currentLanguage.startsWith('ja')) return 'ja'
    if (currentLanguage.startsWith('zh')) return 'zh'
    return 'en' // default to English
  }, [currentLanguage])

  const locationState = location.state as TopicsLocationState
  const autoStart = locationState?.autoStart || false
  const newlyAddedSubtopics = useMemo(
    () => locationState?.newlyAddedSubtopics || [],
    [locationState?.newlyAddedSubtopics]
  )

  const subtopicKeys = useMemo(
    () => [
      'DEMO_TOPICS_SUBTOPICS_CREATE_BATCH',
      'DEMO_TOPICS_SUBTOPICS_UPDATE_BATCH',
      'DEMO_TOPICS_SUBTOPICS_CREATE_LABELS',
      'DEMO_TOPICS_SUBTOPICS_NOTIFY_PARTNER',
    ],
    []
  )

  const subtopics = useMemo(
    () => subtopicKeys.map((key) => intl.formatMessage({id: key})),
    [intl, subtopicKeys]
  )

  const [selectedSubtopicKeys, setSelectedSubtopicKeys] = useState<string[]>([])

  const selectedSubtopics = useMemo(
    () => selectedSubtopicKeys.map((key) => intl.formatMessage({id: key})),
    [selectedSubtopicKeys, intl]
  )

  // Initialize selected keys from preselected subtopics
  useEffect(() => {
    const preSelectedSubtopics = locationState?.preSelectedSubtopics || []
    if (preSelectedSubtopics.length > 0) {
      const keys = preSelectedSubtopics
        .map((text) => {
          const index = subtopics.findIndex((s) => s === text)
          return index >= 0 ? subtopicKeys[index] : null
        })
        .filter((key): key is string => key !== null && subtopicKeys.includes(key))
      setSelectedSubtopicKeys(keys)
    }
  }, [locationState?.preSelectedSubtopics, subtopics, subtopicKeys])

  const [videoDurations, setVideoDurations] = useState<{[key: string]: string}>({})
  const mainTopic = intl.formatMessage({id: 'DEMO_TOPICS_MAIN_TOPIC'})

  const subtopicVideos = useMemo(
    () => ({
      [intl.formatMessage({
        id: 'DEMO_TOPICS_SUBTOPICS_CREATE_BATCH',
      })]: `https://gori-ai.s3.us-west-2.amazonaws.com/shipbae-assets/demo-videos/${language}/a1/create_batch.mp4`,
      [intl.formatMessage({
        id: 'DEMO_TOPICS_SUBTOPICS_UPDATE_BATCH',
      })]: `https://gori-ai.s3.us-west-2.amazonaws.com/shipbae-assets/demo-videos/${language}/a1/update_batch_information.mp4`,
      [intl.formatMessage({
        id: 'DEMO_TOPICS_SUBTOPICS_CREATE_LABELS',
      })]: `https://gori-ai.s3.us-west-2.amazonaws.com/shipbae-assets/demo-videos/${language}/a1/create_shipping_labels.mp4`,
      [intl.formatMessage({
        id: 'DEMO_TOPICS_SUBTOPICS_NOTIFY_PARTNER',
      })]: `https://gori-ai.s3.us-west-2.amazonaws.com/shipbae-assets/demo-videos/${language}/a1/notify_origin_and_schedule_pickup.mp4`,
    }),
    [intl, language]
  )

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    if (minutes > 0) {
      return `${minutes} min ${remainingSeconds} sec`
    }
    return `${remainingSeconds} sec`
  }

  const getVideoDuration = (url: string): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = () => {
        resolve(video.duration)
      }

      video.onerror = () => {
        resolve(60) // fallback to 1 minute if error
      }

      video.src = url
    })
  }

  // No need for language change effect since we use keys

  useEffect(() => {
    const fetchDurations = async () => {
      // Reset durations to prevent showing old language keys
      setVideoDurations({})

      const durations: {[key: string]: string} = {}

      // Process subtopics in parallel for faster loading
      const promises = subtopics.map(async (subtopic) => {
        const videoUrl = subtopicVideos[subtopic as keyof typeof subtopicVideos]

        try {
          const duration = await getVideoDuration(videoUrl)
          durations[subtopic] = formatDuration(duration)
        } catch (error) {
          durations[subtopic] = '1 min 30 sec' // fallback
        }
      })

      await Promise.all(promises)
      setVideoDurations(durations)
    }

    if (subtopics.length > 0 && Object.keys(subtopicVideos).length > 0) {
      fetchDurations()
    }
  }, [subtopics, subtopicVideos, language])

  const isMainTopicSelected = subtopics.every((subtopic) => selectedSubtopics.includes(subtopic))

  const getTotalDuration = useCallback(() => {
    const totalSeconds = selectedSubtopics.reduce((total, subtopic) => {
      const duration = videoDurations[subtopic] || '0 sec'
      const parts = duration.split(' ')
      let seconds = 0

      for (let i = 0; i < parts.length; i += 2) {
        const value = parseInt(parts[i])
        const unit = parts[i + 1]
        if (unit === 'min') {
          seconds += value * 60
        } else if (unit === 'sec') {
          seconds += value
        }
      }

      return total + seconds
    }, 0)

    return formatDuration(totalSeconds)
  }, [selectedSubtopics, videoDurations])

  const handleMainTopicToggle = () => {
    if (isMainTopicSelected) {
      setSelectedSubtopicKeys([])
    } else {
      setSelectedSubtopicKeys([...subtopicKeys])
    }
  }

  const handleSubtopicToggle = (subtopic: string) => {
    const subtopicKey = subtopicKeys[subtopics.indexOf(subtopic)]
    setSelectedSubtopicKeys((prev) => {
      if (prev.includes(subtopicKey)) {
        return prev.filter((k) => k !== subtopicKey)
      } else {
        const newSelected = [...prev, subtopicKey]
        return newSelected.sort((a, b) => subtopicKeys.indexOf(a) - subtopicKeys.indexOf(b))
      }
    })
  }

  const handleStartDemo = useCallback(() => {
    const selectedVideos = selectedSubtopics.map((subtopic) => ({
      title: subtopic,
      url: subtopicVideos[subtopic as keyof typeof subtopicVideos],
      duration: videoDurations[subtopic] || intl.formatMessage({id: 'DEMO_TOPICS_LOADING'}),
    }))

    // Find the index of the first newly added topic to start from
    let startIndex = 0
    if (newlyAddedSubtopics.length > 0) {
      const firstNewlyAddedTopic = newlyAddedSubtopics[0]
      startIndex = selectedSubtopics.indexOf(firstNewlyAddedTopic)
    }

    navigate('/demo/demo-player', {
      state: {
        videos: selectedVideos,
        totalDuration: getTotalDuration(),
        allSubtopics: subtopics,
        selectedSubtopics: selectedSubtopics,
        startVideoIndex: startIndex,
      },
    })
  }, [
    selectedSubtopics,
    videoDurations,
    intl,
    newlyAddedSubtopics,
    navigate,
    getTotalDuration,
    subtopics,
    subtopicVideos,
  ])

  // Auto-start demo if autoStart flag is true
  useEffect(() => {
    if (autoStart && selectedSubtopics.length > 0) {
      // Wait for video durations to load before starting
      const allDurationsLoaded = selectedSubtopics.every(
        (subtopic) =>
          videoDurations[subtopic] &&
          videoDurations[subtopic] !== intl.formatMessage({id: 'DEMO_TOPICS_LOADING'})
      )

      if (allDurationsLoaded) {
        handleStartDemo()
      }
    }
  }, [autoStart, selectedSubtopics, videoDurations, handleStartDemo, intl])

  const resizeTimeoutRef = useRef<NodeJS.Timeout>()

  const equalizeHeights = useCallback(() => {
    const containers = document.querySelectorAll(
      '.demo-subtopic-container'
    ) as NodeListOf<HTMLElement>
    if (containers.length === 0) return

    // Reset heights to get natural dimensions
    containers.forEach((container) => {
      container.style.height = 'auto'
    })

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      const heights = Array.from(containers).map((container) => container.offsetHeight)
      const maxHeight = Math.max(...heights)

      containers.forEach((container) => {
        container.style.height = `${maxHeight}px`
      })
    })
  }, [])

  // Equalize subtopic container heights
  useEffect(() => {
    const debouncedEqualizeHeights = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
      resizeTimeoutRef.current = setTimeout(equalizeHeights, 100)
    }

    // Initial equalization
    equalizeHeights()

    // Handle window resize
    window.addEventListener('resize', debouncedEqualizeHeights, {passive: true})

    return () => {
      window.removeEventListener('resize', debouncedEqualizeHeights)
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [subtopics, language, equalizeHeights])

  return (
    <div className='demo-topics-container'>
      {/* Left Side */}
      <div className='demo-topics-left'>
        <div className='demo-content-container'>
          <img
            src='/media/logos/shipbae-gori-logo-black.svg'
            alt='ShipBae Powered By Gori.ai'
            className='demo-logo'
          />

          <h2 className='demo-topics-title'>{intl.formatMessage({id: 'DEMO_TOPICS_TITLE'})}</h2>

          <div className='demo-topics-card p-4'>
            {/* Main Topic */}
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <div className='d-flex align-items-center'>
                <div
                  className={`demo-checkbox ${isMainTopicSelected ? 'checked' : ''}`}
                  onClick={handleMainTopicToggle}
                />
                <span className='demo-main-topic-text'>{mainTopic}</span>
              </div>
              <span className='demo-subtopics-count'>
                {intl.formatMessage({id: 'DEMO_TOPICS_SUB_TOPICS_COUNT'})}
              </span>
            </div>

            {/* Sub-Topics */}
            <div className='ms-4'>
              <div className='d-flex align-items-center mb-2'>
                <img
                  src='/media/gori/demo/subtopic-arrow.svg'
                  alt='subtopic arrow'
                  className='demo-subtopic-arrow'
                />
                <span className='demo-subtopics-label'>
                  {intl.formatMessage({id: 'DEMO_TOPICS_SUB_TOPICS_LABEL'})}
                </span>
              </div>
              <div className='demo-subtopics-grid' style={{marginLeft: '28px'}}>
                {subtopics.map((subtopic, index) => (
                  <div
                    key={index}
                    className='demo-subtopic-container'
                    onClick={() => handleSubtopicToggle(subtopic)}
                  >
                    <div
                      className={`demo-checkbox-small ${
                        selectedSubtopics.includes(subtopic) ? 'checked' : ''
                      }`}
                    />
                    <span className='demo-subtopic-text'>{subtopic}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Back Button */}
          <div className='demo-content-container'>
            <button
              onClick={() => navigate('/demo/intro-video')}
              className='btn d-flex align-items-center gap-1 demo-back-topics'
            >
              {intl.formatMessage({id: 'DEMO_BACK_BUTTON'})}
            </button>
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div className='demo-topics-right'>
        <DemoPanel
          key={`topics-${language}`}
          mode='topics'
          totalDuration={getTotalDuration()}
          videosCount={selectedSubtopics.length}
          selectedSubtopics={selectedSubtopics}
          mainTopic={mainTopic}
          videoDurations={videoDurations}
        />

        <div className='mt-auto'>
          <button
            onClick={handleStartDemo}
            disabled={selectedSubtopics.length === 0}
            className={`demo-start-button w-100 ${
              selectedSubtopics.length > 0 ? 'enabled' : 'disabled'
            }`}
          >
            {intl.formatMessage({id: 'DEMO_TOPICS_START_DEMO'})}
          </button>
        </div>
      </div>

      <LanguageToggle />
    </div>
  )
}

export default DemoTopicsPage
